import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function CookiePolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 pt-24 pb-16">
        <Card className="max-w-4xl mx-auto bg-slate-800/50 border-slate-700/50 backdrop-blur-sm">
          <CardHeader className="text-center pb-8">
            <CardTitle className="text-4xl font-bold text-white mb-2">
              Cookie Policy
            </CardTitle>
            <p className="text-slate-400">Last updated: January 2025</p>
          </CardHeader>

          <CardContent className="prose prose-invert max-w-none space-y-8 text-slate-300">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. What Are Cookies</h2>
              <p className="mb-4">
                Cookies are small text files that are stored on your computer or mobile device when 
                you visit a website. They are widely used to make websites work more efficiently 
                and to provide information to website owners.
              </p>
              <p>
                This Cookie Policy explains how BirdenMedia ("we," "us," or "our") uses cookies and 
                similar tracking technologies on our website at <span className="text-blue-400">birdenmedia.co</span>.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Types of Cookies */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Types of Cookies We Use</h2>
              
              <h3 className="text-xl font-semibold text-white mb-3">2.1 Essential Cookies</h3>
              <p className="mb-4">
                These cookies are necessary for the website to function properly. They enable basic 
                functions like page navigation and access to secure areas of the website. The website 
                cannot function properly without these cookies.
              </p>
              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/50 mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 text-white">Cookie</th>
                      <th className="text-left py-2 text-white">Purpose</th>
                      <th className="text-left py-2 text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-700/50">
                      <td className="py-2">session_id</td>
                      <td className="py-2">Maintains user session</td>
                      <td className="py-2">Session</td>
                    </tr>
                    <tr>
                      <td className="py-2">csrf_token</td>
                      <td className="py-2">Security - prevents cross-site attacks</td>
                      <td className="py-2">Session</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3">2.2 Analytics Cookies</h3>
              <p className="mb-4">
                These cookies help us understand how visitors interact with our website by collecting 
                and reporting information anonymously. This helps us improve our website.
              </p>
              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/50 mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 text-white">Cookie</th>
                      <th className="text-left py-2 text-white">Purpose</th>
                      <th className="text-left py-2 text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-700/50">
                      <td className="py-2">_ga</td>
                      <td className="py-2">Google Analytics - distinguishes users</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr className="border-b border-slate-700/50">
                      <td className="py-2">_ga_*</td>
                      <td className="py-2">Google Analytics 4 - maintains session state</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr>
                      <td className="py-2">_gid</td>
                      <td className="py-2">Google Analytics - distinguishes users</td>
                      <td className="py-2">24 hours</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3">2.3 Marketing Cookies</h3>
              <p className="mb-4">
                These cookies are used to track visitors across websites. The intention is to display 
                ads that are relevant and engaging for the individual user.
              </p>
              <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700/50">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 text-white">Cookie</th>
                      <th className="text-left py-2 text-white">Purpose</th>
                      <th className="text-left py-2 text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-700/50">
                      <td className="py-2">_fbp</td>
                      <td className="py-2">Meta Pixel - tracks visits across websites</td>
                      <td className="py-2">3 months</td>
                    </tr>
                    <tr>
                      <td className="py-2">_fbc</td>
                      <td className="py-2">Meta Pixel - stores click identifier</td>
                      <td className="py-2">3 months</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Third-Party Cookies */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Third-Party Services</h2>
              <p className="mb-4">
                We use the following third-party services that may set cookies on your device:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <strong className="text-white">Google Analytics:</strong> Web analytics service that 
                  tracks and reports website traffic. 
                  <a href="https://policies.google.com/privacy" className="text-blue-400 hover:text-blue-300 ml-1" target="_blank" rel="noopener noreferrer">
                    Google Privacy Policy
                  </a>
                </li>
                <li>
                  <strong className="text-white">Meta (Facebook) Pixel:</strong> Conversion tracking and 
                  remarketing tool. 
                  <a href="https://www.facebook.com/privacy/policy/" className="text-blue-400 hover:text-blue-300 ml-1" target="_blank" rel="noopener noreferrer">
                    Meta Privacy Policy
                  </a>
                </li>
              </ul>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Managing Cookies */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Managing Cookies</h2>
              <p className="mb-4">
                You have the right to decide whether to accept or reject cookies. You can manage your 
                cookie preferences in the following ways:
              </p>
              
              <h3 className="text-xl font-semibold text-white mb-3">4.1 Browser Settings</h3>
              <p className="mb-4">
                Most web browsers allow you to control cookies through their settings. You can set your 
                browser to refuse cookies or delete certain cookies. Here are links to instructions for 
                common browsers:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li><a href="https://support.google.com/chrome/answer/95647" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">Safari</a></li>
                <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
              </ul>

              <h3 className="text-xl font-semibold text-white mb-3">4.2 Opt-Out Tools</h3>
              <p className="mb-4">
                You can opt out of certain third-party cookies using these tools:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  <a href="https://tools.google.com/dlpage/gaoptout" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">
                    Google Analytics Opt-out Browser Add-on
                  </a>
                </li>
                <li>
                  <a href="https://www.facebook.com/help/568137493302217" className="text-blue-400 hover:text-blue-300" target="_blank" rel="noopener noreferrer">
                    Facebook Ad Preferences
                  </a>
                </li>
              </ul>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Impact of Disabling */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Impact of Disabling Cookies</h2>
              <p>
                If you choose to disable cookies, some features of our website may not function properly. 
                Essential cookies are required for basic website functionality. Disabling analytics and 
                marketing cookies will not affect your ability to use the website, but will limit our 
                ability to improve our services and show you relevant advertising.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Updates */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Changes to This Policy</h2>
              <p>
                We may update this Cookie Policy from time to time to reflect changes in our practices 
                or for other operational, legal, or regulatory reasons. Please revisit this page 
                periodically to stay informed about our use of cookies.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about our use of cookies, please contact us:
              </p>
              <div className="bg-slate-900/50 p-6 rounded-lg border border-slate-700/50">
                <p className="mb-2"><strong className="text-white">Email:</strong> legal@birdenmedia.co</p>
                <p><strong className="text-white">Website:</strong> birdenmedia.co</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900/50 border-t border-slate-800/50 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">
            © {new Date().getFullYear()} BirdenMedia. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
